import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import PinGate, { clearAdminSession } from './PinGate.jsx'
import {
  PbccHeroHeader,
  PbccAdminSidebar,
  PbccSectionTitle,
  PbccPrimaryButton,
} from '../components/PbccLayout.jsx'
import { trialSignOut } from '../auth/TrialGate.jsx'
import AIAssistant from '../components/AIAssistant.jsx'
import { normalizeGhin, isValidGhin } from '../lib/handicap.js'
import { searchCourses } from '../lib/golfCourseApi.js'
import { lookupGhinHandicap } from '../lib/ghinLookup.js'
import {
  watchClubCourses, saveClubCourse, deleteClubCourse,
  watchClubPlayers, saveClubPlayer, updateClubPlayer, deleteClubPlayer,
} from '../lib/data.js'
// Direct firebase imports — the Edit Course modal writes an updated
// record back to /club/courses/<id>. The existing data.js helpers
// only support create + delete, and the spec for this change limits
// the edit to a single file (this one), so we wire the update call
// inline rather than adding an updateClubCourse helper to data.js.
import { ref, update } from 'firebase/database'
import { db } from '../lib/firebase.js'

// Club-wide data: a catalog of frequently-played courses and a roster
// of repeat players. Both live under /club/* in RTDB so they're shared
// across every tournament without ever mutating tournament records.
export default function ClubData() {
  return (
    <PinGate>
      {/* Content only — AdminShell provides the sidebar + zoom frame. */}
      <>
        <div className="pbcc-main-panel-4ball flex-1 flex flex-col min-h-0 min-w-0">
          <PbccHeroHeader
            variant="admin"
            showCommandCenter={false}
            onLock={() => { clearAdminSession(); location.reload() }}
            onSignOut={() => { clearAdminSession(); trialSignOut() }}
          />
          <div className="flex flex-1 min-h-0">
            <main className="max-w-6xl w-full mx-auto p-4 sm:p-5 space-y-6 flex flex-col flex-1 min-h-0">
              <PbccSectionTitle title="Handicap & Courses" subtitle="Saved courses and player roster — shared across every tournament." />
              <CoursesSection />
              <PlayersSection />
            </main>
          </div>
        </div>
      </>
      <AIAssistant tournamentId={null} />
    </PinGate>
  )
}

// ---------------- Courses ----------------
function CoursesSection() {
  const [courses, setCourses] = useState({})
  // Two tabs at the top of the courses card. 'saved' is the default
  // since that's the everyday view; 'add' is where the director goes
  // to grow the roster. After a successful add we flip back to 'saved'
  // so the director immediately sees the new row.
  const [tab, setTab] = useState('saved')
  const [editing, setEditing] = useState(null)
  useEffect(() => watchClubCourses(setCourses), [])
  const list = Object.entries(courses)
    .map(([id, v]) => ({ id, ...(v || {}) }))
    .sort((a, b) => (a.courseName || '').localeCompare(b.courseName || ''))

  const remove = async (id, name) => {
    if (!confirm(`Remove "${name}" from saved courses?`)) return
    await deleteClubCourse(id)
  }

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-bold border-b-2 transition ${
        tab === key
          ? 'border-green-deep text-green-deep'
          : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}>
      {label}
    </button>
  )

  return (
    <section className="card">
      <div className="border-b mb-4 flex gap-2">
        {tabBtn('add', '+ Add Course')}
        {tabBtn('saved', `Saved Courses (${list.length})`)}
      </div>

      {tab === 'add' ? (
        <AddCourseInline onSaved={() => setTab('saved')} />
      ) : (
        <SavedCoursesList
          list={list}
          onRemove={remove}
          onEdit={(c) => setEditing(c)}
          onSwitchToAdd={() => setTab('add')} />
      )}
      {editing ? (
        <EditCourseModal course={editing} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  )
}

function SavedCoursesList({ list, onRemove, onEdit, onSwitchToAdd }) {
  if (list.length === 0) {
    return (
      <p className="text-gray-500 text-sm">
        No courses saved yet.{' '}
        <button type="button" onClick={onSwitchToAdd} className="text-green-deep font-semibold hover:underline">
          Add your first course
        </button>{' '}
        so it appears as a quick pick when creating a tournament.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-sm uppercase text-gray-500 border-b">
          <tr>
            <th className="text-left py-2">Course</th>
            <th className="text-right py-2">Par</th>
            <th className="text-left py-2" colSpan={2}>Tee Boxes &amp; Stroke Index</th>
            <th className="text-right py-2"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => {
            const tees = teeListFor(c)
            // Both stroke-index arrays are optional. Fall back to the
            // legacy single `strokeIndex` field for older records that
            // pre-date the men/women split.
            const siMen = Array.isArray(c.strokeIndexMen)
              ? c.strokeIndexMen
              : (Array.isArray(c.strokeIndex) ? c.strokeIndex : null)
            const siWomen = Array.isArray(c.strokeIndexWomen) ? c.strokeIndexWomen : null
            return (
              <tr key={c.id} className="border-b last:border-0 align-top">
                <td className="py-2 font-semibold">{c.courseName || '—'}</td>
                <td className="py-2 text-right">{c.coursePar ?? '—'}</td>
                <td className="py-2" colSpan={2}>
                  <TeeGroups
                    teeBoxes={tees}
                    siMen={siMen}
                    siWomen={siWomen} />
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button
                    className="text-sm font-semibold hover:underline mr-3"
                    onClick={() => onEdit(c)}>
                    Edit
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800 text-sm font-semibold"
                    onClick={() => onRemove(c.id, c.courseName || 'this course')}>
                    Remove
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function AddCourseInline({ onSaved }) {
  // Course search state — debounced query + results dropdown.
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchErr, setSearchErr] = useState('')
  const debRef = useRef(null)

  // Selected course + which tee (if any) the director highlighted.
  const [course, setCourse] = useState(null)   // { id, name, par, teeBoxes }
  const [highlightedIdx, setHighlightedIdx] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current)
    const q = (query || '').trim()
    if (q.length < 3 || (course && q === course.name)) {
      setResults([])
      setSearching(false)
      return
    }
    debRef.current = setTimeout(async () => {
      setSearching(true); setSearchErr('')
      try {
        const hits = await searchCourses(q)
        setResults(hits)
        setSearchOpen(true)
        if (hits.length === 0) setSearchErr('No matches — try fewer words.')
      } catch {
        setSearchErr('Search failed — check connection and retry.')
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => debRef.current && clearTimeout(debRef.current)
  }, [query, course?.name])

  const pickCourse = (hit) => {
    setCourse(hit)
    setQuery(hit.name)
    setResults([])
    setSearchOpen(false)
    setHighlightedIdx(null)
  }

  const tees = course?.teeBoxes || []
  const canSave = !!course && tees.length > 0

  const save = async () => {
    if (!canSave) return
    setBusy(true)
    try {
      // If a tee is highlighted, persist only that one. Otherwise the
      // entire tee list goes — director picks the playing tee at
      // tournament setup.
      const toSave = highlightedIdx == null
        ? tees
        : [tees[highlightedIdx]]
      await saveClubCourse({
        courseName: course.name,
        courseId: course.id || null,
        coursePar: Number(course.par) || null,
        teeBoxes: toSave.map((t) => ({
          name: t.name,
          rating: Number(t.rating) || 0,
          slope: Number(t.slope) || 0,
          par: Number(t.par) || Number(course.par) || 72,
        })),
      })
      // Reset local state so the tab is clean if the director comes
      // back to add another course later in the same session.
      setQuery('')
      setCourse(null)
      setHighlightedIdx(null)
      setResults([])
      onSaved?.()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {/* Course search */}
      <div className="relative">
        <label className="block">
          <span className="text-sm uppercase tracking-widest font-bold text-gray-600">Course</span>
          <input
            className="input mt-1"
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (course && e.target.value !== course.name) setCourse(null) }}
            onFocus={() => results.length > 0 && setSearchOpen(true)}
            placeholder="Type at least 3 letters to search…"
            autoComplete="off" />
        </label>
        {searching ? <p className="text-sm text-gray-500 mt-1">Searching…</p> : null}
        {searchErr && !searching ? <p className="text-sm text-red-600 mt-1">{searchErr}</p> : null}
        {searchOpen && results.length > 0 ? (
          <div className="absolute z-10 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => pickCourse(r)}
                className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0">
                <div className="font-semibold text-sm">{r.name}</div>
                <div className="text-sm text-gray-500">
                  {r.location || '—'} · Par {r.par} · {r.teeBoxes.length} tee{r.teeBoxes.length === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Tee box preview — clickable to highlight just one. */}
      {course ? (
        <div className="mt-4">
          <div className="flex justify-between items-baseline mb-1.5">
            <span className="text-sm uppercase tracking-widest font-bold text-gray-600">Tee Boxes</span>
            {highlightedIdx != null ? (
              <button
                type="button"
                onClick={() => setHighlightedIdx(null)}
                className="text-sm font-semibold text-gray-500 hover:text-gray-800 underline">
                Clear selection — save all
              </button>
            ) : null}
          </div>
          {tees.length === 0 ? (
            <p className="text-sm text-gray-500">No tee data published for this course.</p>
          ) : (
            <div className="space-y-1.5">
              {tees.map((t, i) => {
                const active = highlightedIdx === i
                return (
                  <button
                    key={`${t.name}-${i}`}
                    type="button"
                    onClick={() => setHighlightedIdx(active ? null : i)}
                    className={`w-full flex justify-between items-center px-3 py-2 rounded-lg border text-left transition ${
                      active
                        ? 'bg-green-50 border-green-600 ring-2 ring-green-500/40'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}>
                    <span className="font-semibold text-sm">{t.name}</span>
                    <span className="text-sm text-gray-600">
                      Rating {t.rating} / Slope {t.slope} / Par {t.par || course.par}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
          <p className="text-sm text-gray-600 mt-2 leading-snug">
            {highlightedIdx == null
              ? `All ${tees.length} tee box${tees.length === 1 ? '' : 'es'} will be saved. Tap a row to save just that one. Tee box per player is picked when setting up each tournament.`
              : `Only “${tees[highlightedIdx]?.name}” will be saved.`}
          </p>
        </div>
      ) : null}

      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          className="btn-primary"
          disabled={!canSave || busy}
          onClick={save}>
          {busy ? 'Saving…' : (highlightedIdx == null && tees.length > 1 ? 'Save All Tees' : 'Save Course')}
        </button>
      </div>
    </div>
  )
}

// Normalize a saved course record into a tee list. Prefers the new
// `teeBoxes` array; falls back to synthesizing a single-element array
// from the older single-tee shape so previously-saved courses still
// render and still quick-pick correctly.
function teeListFor(c) {
  if (Array.isArray(c?.teeBoxes) && c.teeBoxes.length > 0) {
    return c.teeBoxes.map((t) => ({
      name: t.name || '',
      rating: Number(t.rating) || 0,
      slope: Number(t.slope) || 0,
      par: Number(t.par) || Number(c.coursePar) || 72,
      group: t.group || null,
    }))
  }
  if (c?.teeName && Number(c?.slopeRating) > 0 && Number(c?.courseRating) > 0) {
    return [{
      name: c.teeName,
      rating: Number(c.courseRating),
      slope: Number(c.slopeRating),
      par: Number(c.coursePar) || 72,
    }]
  }
  return []
}

// Saved-courses display: tee boxes grouped by player class. The
// "senior" group reuses the women's SI (PBCC's spec: Senior Men and
// Women share the same stroke index). Each section renders the
// matching tees on top with the corresponding 18-hole SI underneath.
const TEE_GROUP_SECTIONS = [
  { key: 'mens',   label: "Men's",        siFrom: 'men'   },
  { key: 'senior', label: "Senior Men's", siFrom: 'women' },
  { key: 'womens', label: "Women's",      siFrom: 'women' },
]

function TeeGroups({ teeBoxes, siMen, siWomen }) {
  if (!teeBoxes || teeBoxes.length === 0) {
    return <span className="text-gray-400">—</span>
  }
  // Untagged tees (legacy records before the group field existed) fall
  // into a single bucket so they still render. Once the migration is
  // run for a course, this fallback bucket is empty.
  const byGroup = { mens: [], senior: [], womens: [], untagged: [] }
  for (const t of teeBoxes) {
    const g = t.group && byGroup[t.group] ? t.group : 'untagged'
    byGroup[g].push(t)
  }
  const sections = TEE_GROUP_SECTIONS.filter((sec) => byGroup[sec.key].length > 0)
  return (
    <div className="space-y-4">
      {sections.map((sec) => {
        const si = sec.siFrom === 'men' ? siMen : siWomen
        return (
          <div key={sec.key}>
            <div className="text-sm uppercase tracking-widest font-bold text-gray-600 mb-1">
              {sec.label}
            </div>
            <div className="space-y-0.5 mb-1.5">
              {byGroup[sec.key].map((t, i) => (
                <div key={`${t.name}-${i}`} className="text-sm">
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-gray-500"> — Rating {t.rating} / Slope {t.slope} / Par {t.par}</span>
                </div>
              ))}
            </div>
            {si ? <SiPerHole label={`${sec.label} stroke index`} arr={si} /> : (
              <span className="text-sm text-gray-400 italic">stroke index not set</span>
            )}
          </div>
        )
      })}
      {byGroup.untagged.length > 0 ? (
        <div>
          <div className="text-sm uppercase tracking-widest font-bold text-gray-500 mb-1">
            Untagged
          </div>
          <div className="space-y-0.5">
            {byGroup.untagged.map((t, i) => (
              <div key={`${t.name}-${i}`} className="text-sm">
                <span className="font-semibold">{t.name}</span>
                <span className="text-gray-500"> — Rating {t.rating} / Slope {t.slope} / Par {t.par}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-400 italic mt-1">
            Open Edit to assign a group (Men's / Senior Men's / Women's).
          </p>
        </div>
      ) : null}
    </div>
  )
}

// Compact per-hole stroke-index display for the Saved Courses table.
// Renders an 18-cell grid (1 row · 18 cols) with each hole number
// above its SI value so the director can scan H1–H18 at a glance.
function SiPerHole({ label, arr }) {
  return (
    <div>
      <div className="text-sm uppercase tracking-widest font-bold text-gray-500 mb-0.5">
        {label}
      </div>
      <div className="grid grid-cols-18 gap-px text-sm" style={{ gridTemplateColumns: 'repeat(18, minmax(0, 1fr))' }}>
        {Array.from({ length: 18 }, (_, i) => (
          <div key={i} className="text-center">
            <div className="text-sm text-gray-400 leading-none">H{i + 1}</div>
            <div className="font-semibold text-gray-800 leading-none mt-0.5">
              {arr[i] ?? '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Modal for editing a saved course. Hydrates from the persisted record
// and lets the director update course name, total par, every tee box
// (name / rating / slope / par), and the 18-hole stroke index. Save
// writes the whole record back to /club/courses/<id> via a direct
// firebase update — the spec restricts this change to ClubData.jsx so
// we can't reach into data.js to add an updateClubCourse helper.
function EditCourseModal({ course, onClose }) {
  const [courseName, setCourseName] = useState(course.courseName || '')
  const [coursePar, setCoursePar] = useState(
    course.coursePar != null ? String(course.coursePar) : '',
  )
  const initialTees = teeListFor(course)
  const [tees, setTees] = useState(
    initialTees.length > 0
      ? initialTees.map((t) => ({
          name: t.name || '',
          rating: String(t.rating || ''),
          slope: String(t.slope || ''),
          par: String(t.par || ''),
          group: t.group || 'mens',
        }))
      : [{ name: '', rating: '', slope: '', par: '', group: 'mens' }],
  )
  // Stroke index — two separate arrays so men's and women's SIs can
  // differ (PBCC has different SIs for the Red tees vs the rest).
  // Hydrate each from the matching field; legacy `strokeIndex` records
  // (single shared array) seed the men's grid so older data isn't lost.
  const hydrateSI = (src) => {
    const arr = Array.isArray(src) ? src : []
    return Array.from({ length: 18 }, (_, i) => (arr[i] != null ? String(arr[i]) : ''))
  }
  const [siMen, setSiMen] = useState(
    hydrateSI(course.strokeIndexMen || course.strokeIndex),
  )
  const [siWomen, setSiWomen] = useState(hydrateSI(course.strokeIndexWomen))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const updateTee = (i, patch) => {
    setTees((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)))
  }
  const updateSI = (setter) => (i, val) => {
    const clean = val.replace(/\D/g, '').slice(0, 2)
    setter((prev) => prev.map((v, idx) => (idx === i ? clean : v)))
  }
  const updateSiMen = updateSI(setSiMen)
  const updateSiWomen = updateSI(setSiWomen)

  // Pull out the numeric values from a string-array SI grid for
  // validation + persistence. Returns null when nothing is filled.
  const finalizeSI = (grid) => {
    const nums = grid.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0)
    if (nums.length === 0) return { nums, value: null, complete: false }
    return {
      nums,
      value: grid.map((v) => Number(v) || 0),
      complete: nums.length === 18,
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!courseName.trim()) { setErr('Course name is required.'); return }
    const men = finalizeSI(siMen)
    const women = finalizeSI(siWomen)
    // Soft-validate each grid independently.
    for (const [label, info] of [['Men\'s', men], ['Women\'s', women]]) {
      const bad = info.nums.some((n) => n < 1 || n > 18)
      if (bad) { setErr(`${label} stroke index values must be between 1 and 18.`); return }
    }
    const menDupes = men.nums.length !== new Set(men.nums).size
    const womenDupes = women.nums.length !== new Set(women.nums).size
    if ((menDupes || womenDupes) && !confirm('Stroke index has duplicates. Save anyway?')) return

    setBusy(true)
    setErr('')
    try {
      const payload = {
        courseName: courseName.trim(),
        courseId: course.courseId || null,
        coursePar: coursePar === '' ? null : Number(coursePar),
        teeBoxes: tees
          .filter((t) => (t.name || '').trim() && Number(t.rating) > 0 && Number(t.slope) > 0)
          .map((t) => ({
            name: t.name.trim(),
            rating: Number(t.rating) || 0,
            slope: Number(t.slope) || 0,
            par: Number(t.par) || Number(coursePar) || 72,
            group: t.group || 'mens',
          })),
        // Only persist a grid when all 18 slots are filled. Partial /
        // empty saves as null so downstream code can fall back to a
        // default.
        strokeIndexMen: men.complete ? men.value : null,
        strokeIndexWomen: women.complete ? women.value : null,
        savedAt: course.savedAt || Date.now(),
        updatedAt: Date.now(),
      }
      await update(ref(db, `club/courses/${course.id}`), payload)
      onClose()
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell title="Edit Course" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Course Name *">
          <input className="input" value={courseName} onChange={(e) => setCourseName(e.target.value)} />
        </Field>
        <Field label="Course Par">
          <input
            className="input"
            type="number"
            value={coursePar}
            min="60" max="80"
            onChange={(e) => setCoursePar(e.target.value)}
            placeholder="e.g. 72" />
        </Field>

        <div>
          <p className="text-sm uppercase tracking-widest font-bold text-gray-600 mb-1.5">
            Tee Boxes
          </p>
          <div className="space-y-2">
            {tees.map((t, i) => (
              <div key={i} className="grid grid-cols-14 gap-2 items-center" style={{ gridTemplateColumns: 'repeat(14, minmax(0, 1fr))' }}>
                <input
                  className="input col-span-3 text-sm"
                  placeholder="Tee name"
                  value={t.name}
                  onChange={(e) => updateTee(i, { name: e.target.value })} />
                <input
                  className="input col-span-2 text-sm"
                  type="number" step="0.1"
                  placeholder="Rating"
                  value={t.rating}
                  onChange={(e) => updateTee(i, { rating: e.target.value })} />
                <input
                  className="input col-span-2 text-sm"
                  type="number"
                  placeholder="Slope"
                  value={t.slope}
                  onChange={(e) => updateTee(i, { slope: e.target.value })} />
                <input
                  className="input col-span-2 text-sm"
                  type="number"
                  placeholder="Par"
                  value={t.par}
                  onChange={(e) => updateTee(i, { par: e.target.value })} />
                <select
                  className="input col-span-3 text-sm"
                  value={t.group || 'mens'}
                  onChange={(e) => updateTee(i, { group: e.target.value })}>
                  <option value="mens">Men's</option>
                  <option value="senior">Senior Men's</option>
                  <option value="womens">Women's</option>
                </select>
                <button
                  type="button"
                  className="col-span-2 text-red-600 text-sm font-semibold hover:underline"
                  onClick={() => setTees((prev) => prev.filter((_, idx) => idx !== i))}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="text-sm font-semibold text-green-deep hover:underline mt-2"
            onClick={() => setTees((prev) => [...prev, { name: '', rating: '', slope: '', par: '', group: 'mens' }])}>
            + Add tee box
          </button>
        </div>

        <div>
          <p className="text-sm uppercase tracking-widest font-bold text-gray-600 mb-1.5">
            Stroke Index — per hole 1–18
          </p>
          <p className="text-sm text-gray-500 mb-3 leading-snug">
            Numbers 1–18 ranking holes by handicap difficulty (1 = hardest).
            Men's and women's are stored separately because some courses
            use a different SI from the forward tees.
          </p>
          <p className="text-sm uppercase tracking-widest font-bold text-gray-600 mt-1 mb-1.5">
            Men's (Gold / Blue / White tees)
          </p>
          <div className="grid grid-cols-6 gap-2 mb-3">
            {siMen.map((v, i) => (
              <label key={i} className="block">
                <span className="text-sm uppercase tracking-widest text-gray-500 font-bold">H{i + 1}</span>
                <input
                  className="input mt-0.5 text-center"
                  type="number"
                  inputMode="numeric"
                  min="1" max="18"
                  value={v}
                  onChange={(e) => updateSiMen(i, e.target.value)}
                  placeholder="—" />
              </label>
            ))}
          </div>
          <p className="text-sm uppercase tracking-widest font-bold text-gray-600 mt-1 mb-1.5">
            Women's (Red tees)
          </p>
          <div className="grid grid-cols-6 gap-2">
            {siWomen.map((v, i) => (
              <label key={i} className="block">
                <span className="text-sm uppercase tracking-widest text-gray-500 font-bold">H{i + 1}</span>
                <input
                  className="input mt-0.5 text-center"
                  type="number"
                  inputMode="numeric"
                  min="1" max="18"
                  value={v}
                  onChange={(e) => updateSiWomen(i, e.target.value)}
                  placeholder="—" />
              </label>
            ))}
          </div>
        </div>

        {err ? <p className="text-red-600 text-sm">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ---------------- Players ----------------
function PlayersSection() {
  const [players, setPlayers] = useState({})
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [query, setQuery] = useState('')
  useEffect(() => watchClubPlayers(setPlayers), [])

  const list = useMemo(() => {
    const arr = Object.entries(players).map(([id, v]) => ({ id, ...(v || {}) }))
    const q = query.trim().toLowerCase()
    const filtered = q
      ? arr.filter((p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.email || '').toLowerCase().includes(q) ||
          (p.ghin || '').includes(q),
        )
      : arr
    return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [players, query])

  const remove = async (id, name) => {
    if (!confirm(`Remove ${name || 'this player'} from the roster?`)) return
    await deleteClubPlayer(id)
  }

  return (
    <section className="card">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <h3 className="text-lg font-bold">Player Roster ({list.length})</h3>
        <PbccPrimaryButton onClick={() => setAdding(true)}>+ Add Player</PbccPrimaryButton>
      </div>
      <div className="mb-3">
        <input
          className="input"
          placeholder="Search by name, email, or handicap #…"
          value={query}
          onChange={(e) => setQuery(e.target.value)} />
      </div>
      {list.length === 0 ? (
        <p className="text-gray-500 text-sm">
          {query
            ? 'No players match that search.'
            : 'No players saved yet. Add your regulars so they pop up as quick picks when adding players to a tournament.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-sm uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Handicap #</th>
                <th className="text-right py-2">Idx</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 font-semibold">{p.name}</td>
                  <td>{p.email || '—'}</td>
                  <td>{p.ghin || '—'}</td>
                  <td className="text-right">{p.handicapIndex ?? '—'}</td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      className="text-sm font-semibold hover:underline mr-3"
                      onClick={() => setEditing(p)}>
                      Edit
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800 text-sm font-semibold"
                      onClick={() => remove(p.id, p.name)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding ? <AddOrEditPlayerModal onClose={() => setAdding(false)} /> : null}
      {editing ? <AddOrEditPlayerModal existing={editing} onClose={() => setEditing(null)} /> : null}
    </section>
  )
}

function AddOrEditPlayerModal({ existing, onClose }) {
  const [name, setName] = useState(existing?.name || '')
  const [email, setEmail] = useState(existing?.email || '')
  const [ghin, setGhin] = useState(existing?.ghin || '')
  const [handicapIndex, setHandicapIndex] = useState(
    existing?.handicapIndex != null ? String(existing.handicapIndex) : '',
  )
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // Live GHIN lookup status — drives the loading/notfound message and
  // hides the manual Index field on success.
  const [ghinStatus, setGhinStatus] = useState('idle')
  const runGhinLookup = async () => {
    const clean = (ghin || '').replace(/\D/g, '')
    if (clean.length < 7) {
      setGhinStatus(clean.length === 0 ? 'idle' : 'notfound')
      return
    }
    setGhinStatus('loading')
    const res = await lookupGhinHandicap(clean)
    if (res.ok) {
      setHandicapIndex(String(res.handicapIndex))
      // Auto-fill the player's name from the GHIN record. Only
      // overwrites when GHIN actually returned one — preserves any
      // name the admin already typed if the API didn't carry one.
      if (res.fullName) setName(res.fullName)
      setGhinStatus('ok')
    } else {
      setGhinStatus(res.reason || 'notfound')
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) { setErr('Name is required.'); return }
    if (ghin && !isValidGhin(ghin)) {
      setErr('Handicap (GH) ID must be 7–10 digits.')
      return
    }
    setBusy(true)
    setErr('')
    try {
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        ghin: normalizeGhin(ghin) || null,
        handicapIndex: handicapIndex === '' ? null : Number(handicapIndex),
      }
      if (existing) await updateClubPlayer(existing.id, payload)
      else await saveClubPlayer(payload)
      onClose()
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell title={existing ? 'Edit Player' : 'Add Player'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name *">
          <input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email">
          <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" />
        </Field>
        <Field label="Handicap (GH) ID">
          <input
            className="input"
            inputMode="numeric"
            value={ghin}
            onChange={(e) => {
              setGhin(e.target.value.replace(/\D/g, '').slice(0, 10))
              if (ghinStatus !== 'idle') setGhinStatus('idle')
            }}
            onBlur={runGhinLookup}
            placeholder="7–10 digits"
            maxLength={10} />
        </Field>
        <ClubGhinStatusLine status={ghinStatus} index={handicapIndex} />
        {ghinStatus !== 'ok' ? (
          <Field label="Handicap Index">
            <input type="number" step="0.1" className="input" value={handicapIndex} onChange={(e) => setHandicapIndex(e.target.value)} placeholder="e.g. 12.4" />
          </Field>
        ) : null}
        {err ? <p className="text-red-600 text-sm">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : (existing ? 'Save Changes' : 'Add Player')}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ---------------- Shared ----------------
function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <h3 className="font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm uppercase tracking-widest font-bold text-gray-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

// Per-row status under the GHIN field in the Club roster modal. Light
// theme matched to the surrounding ModalShell.
function ClubGhinStatusLine({ status, index }) {
  if (status === 'loading') {
    return <p className="text-sm text-gray-600" role="status">Looking up handicap…</p>
  }
  if (status === 'ok') {
    return (
      <p className="text-sm font-bold text-green-700" role="status">
        ✓ Handicap Index pulled from handicap lookup: <span className="font-extrabold">{index}</span>
      </p>
    )
  }
  if (status === 'notfound') {
    return (
      <p className="text-sm text-red-600" role="status">
        Handicap number not found — enter the Handicap Index manually below.
      </p>
    )
  }
  if (status === 'auth' || status === 'network') {
    return (
      <p className="text-sm text-red-600" role="status">
        Couldn't reach the handicap lookup service — enter the Handicap Index manually below.
      </p>
    )
  }
  return null
}
